import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getArticleConfiguration } from "../../shared/utils/getArticleInformation"
import { getCustomer } from "../../shared/utils/getCustomer"
import { getSkuCalculatedPrice } from "../../shared/utils/getPrices"
import { getStoreKeysWithChannels } from "../../shared/utils/getStoreKeysWithChannels"
import { addCustomLineItemAction, removeCustomLineItemAction } from "../utils/cartHelpers"
import { getCart } from "../utils/getCart"

export const mutationRemoveCustomLineItems = {
  async removeCustomLineItems(parent, args, context, info) {
    let { cartKey, cartId, items = [], allItems, lang, cdn } = args
    let actions = []
    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.CARTS,
      operation: Operations.UPDATE,
    })

    const commerceApi = CommerceService.api()
    const currentCart = await getCart(cartId, cartKey)

    if (!currentCart || currentCart.cartState !== "Active") {
      throw new Error("Cart was not found or is not in active state")
    }

    if (currentCart.customLineItems <= 0) {
      throw new Error("Cart does not have any line items")
    }

    const { customLineItems, customerId, store } = currentCart
    const customer = await getCustomer({ id: customerId })

    if (!customer) {
      throw new Error("Customer not found")
    }

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    const { stores: customerStores, customerGroup } = customer

    const storeKeysWithChannels = await getStoreKeysWithChannels(customerStores)

    // since it could be that all items are removed all at once, go over each of the custom line items
    await Promise.all(
      customLineItems.map(async (customLineItem) => {
        // remove all of the items in case that was requested
        if (allItems) {
          actions.push(removeCustomLineItemAction(customLineItem?.id))

          return
        }

        const targetItem = items?.find((item) => item?.slug === customLineItem?.slug)

        if (!targetItem) return

        const targetCustomFields = customLineItem?.custom?.fields

        if (!targetCustomFields) return

        const articleConfig = await getArticleConfiguration({
          sku: targetCustomFields.sku,
          productKey: targetCustomFields.productId,
        })
        if (!articleConfig) {
          actions.push(removeCustomLineItemAction(customLineItem?.id))

          return
        }

        let itemQuantity = Number(targetItem?.quantity) || 0
        if (articleConfig?.sellableInDecimals) {
          itemQuantity = Math.round(itemQuantity * 1000)
        }

        const updatedQuantity = Number(customLineItem.quantity) - itemQuantity
        actions.push(removeCustomLineItemAction(customLineItem?.id))

        // either quantity is 0 or no custom fields in both cases
        // remove the line item as there is nothing to do anymore
        if (updatedQuantity === 0) return

        const lineItemCustomPrice =
          typeof targetCustomFields?.customPrice?.centAmount === "number"
            ? targetCustomFields?.customPrice?.centAmount
            : null

        // recalculate the price in case configuration in commercetools changed
        const price = await getSkuCalculatedPrice({
          sku: targetCustomFields?.sku,
          channels: storeKeysWithChannels,
          group: customerGroup,
          country: currentCart?.billingAddress?.country,
          discountPercent: targetCustomFields?.discountPercent,
          customPrice: lineItemCustomPrice,
          currencyCode: customLineItem?.money?.currencyCode,
          articleConfig,
          quantity: updatedQuantity,
        })

        if (!price) return

        actions.push(
          addCustomLineItemAction(customLineItem, customLineItem?.money?.currencyCode, price, cartId, updatedQuantity),
        )
      }),
    )

    // remove all null items
    actions = actions.filter((n) => n)

    // no need for any action, since nothing was matched
    if (actions?.length <= 0) {
      return currentCart
    }

    actions = clearCartCustomFieldsIfEmpty(actions, currentCart)

    const payload = {
      version: currentCart.version,
      actions,
      ...(store && { storeKey: store.key }),
    }

    let updatedCart = (
      await commerceApi
        .carts()
        .withId({ ID: currentCart.id })
        .post({
          body: JSON.stringify(payload),
        })
        .execute()
    )?.body

    return updatedCart
  },
}

/**
 * Clears reservation and picklist numbers from cart when all items are being removed.
 * This ensures that when the cart is emptied (either via "Empty Cart" or by manually
 * removing all line items), the picklist/reservation editing mode is also cleared.
 *
 * @param {array} actions - The cart update actions
 * @param {object} cart - The current cart
 * @returns {array} - Actions with optional setCustomType action added
 */
const clearCartCustomFieldsIfEmpty = (actions, cart) => {
  if (!cart?.custom?.fields || !cart?.custom?.type?.id) return actions

  const countLineItems = cart?.customLineItems?.length || 0
  const removeActions = actions.filter((action) => action.action === "removeCustomLineItem")

  // Only clear if we're removing all items and not adding any back
  const hasAddActions = actions.some((action) => action.action === "addCustomLineItem")
  const isRemovingAllItems = removeActions.length === countLineItems

  if (hasAddActions || !isRemovingAllItems) {
    return actions
  }

  const { createdByEmployeeNumber, freightSurcharge } = cart.custom.fields

  // When emptying the cart, clear both reservationNumber and picklistNumber
  // by not including them in the fields object
  const cartAction = {
    action: "setCustomType",
    type: {
      typeId: "type",
      id: cart.custom.type.id,
    },
    fields: {
      createdByEmployeeNumber,
      ...(freightSurcharge && { freightSurcharge }),
    },
  }

  return [...actions, cartAction]
}
